import { reactMount } from '@/lib/reactMount';
import { useRef, useState } from 'react';
import {
  CheckIcon,
  UserIcon,
  UsersIcon,
  UserGroupIcon,
  ArrowLongRightIcon,
  PlusIcon,
  StarIcon,
  MinusIcon,
} from '@heroicons/react/24/solid';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { Button } from '@/components/Button';
import { useSessionStorage } from 'react-use';
import { DivMotion, HeaderMotion } from '@/components/motionComponents';
import { animationSettings } from '@/animationSettings';
import { useIsViewPortLessOrEqualTo } from '@/hooks/useIsViewPortLessOrEqualTo';
import {
  getPricingRegionByCountryIso,
  PricingRegion,
} from '@/lib/pricingMatrix/PricingRegion';
import { TimelyPlanName } from '@/lib/pricingMatrix/TimelyPlanName';
import {
  getPlanByPricingRegion,
  getPlanPrice,
} from '@/lib/pricingMatrix/PlanPricing';
import { REGISTER_PAGE_URL } from '@/Views/constants/constants';

/* The client dictated the order of the regions */
export const RegionLabels = {
  [PricingRegion.AUS]: 'Australia (exc GST)',
  [PricingRegion.NZL]: 'New Zealand (exc GST)',
  [PricingRegion.GBR]: 'Great Britain',
  [PricingRegion.USA]: 'USA',
  [PricingRegion.IRL]: 'Ireland',
  [PricingRegion.EUE]: 'Europe',
  [PricingRegion.CAN]: 'Canada',
  [PricingRegion.ROW]: 'Rest of the world',
};

const _plans = [
  TimelyPlanName.BUILD,
  TimelyPlanName.ELEVATE,
  TimelyPlanName.INNOVATE,
];

const numberToString = (number) => {
  // returns String because we want to keep the trailing zeros: .5 will show as .50
  return number % 1 ? number.toFixed(2) : String(number);
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const maxStaff = 20;

const PricePlansV1 = ({ dataset, umbraco }) => {
  const umbracoRef = useRef(umbraco || JSON.parse(dataset.umbraco));
  umbraco ||= umbracoRef.current;
  const [pricingRegion, setPricingRegion] = useState(
    getPricingRegionByCountryIso(
      window.localStorage.getItem('countryIso') ||
        window.localStorage.getItem('regionIso'),
    ),
  );
  const [staffCount, setStaffCount] = useSessionStorage('staffCount', '1');

  const {
    standardTransition,
    standardViewportTrigger,
    standardTransitionDuration,
    standardTransitionDelay,
    standardTransitionDistance,
    standardTransitionEasing,
    standardTransitionStagger,
  } = animationSettings();

  return (
    <section id={umbraco?.AnchorTarget || null} className="bg-charcoal-50">
      <HeaderMotion
        initial={{ opacity: 0.1 }}
        whileInView={{ opacity: 1 }}
        transition={standardTransition}
        viewport={{ once: true, amount: standardViewportTrigger }}
        className={`text-center pt-16 pb-[240px] ${umbraco.IsSimplifiedVersion ? 'bg-charcoal-50' : 'bg-midnight-50'}`}
      >
        <div className="container mx-auto max-w-5xl px-4 md:px-8 lg:px-10 pt-2.5">
          {umbraco?.SmallTitle && (
            <p className="text-pretty mb-3 text-base text-midnight-500">
              {umbraco?.SmallTitle}
            </p>
          )}

          {umbraco?.MainTitle && (
            <h2 className="mb-4 text-4xl md:text-5xl text-pretty font-serif tracking-tight text-charcoal-800">
              {umbraco?.MainTitle}
            </h2>
          )}

          {/*
              May be used in the future as it was designed for it.
              {hardCoded.Summary && (
                <div
                  className='_rte text-base text-midnight-800 max-w-[556px] mx-auto font-light'
                  dangerouslySetInnerHTML={{ __html: hardCoded.Summary }}
                />
              )}
            */}

          <StaffCounter staffCount={staffCount} setStaffCount={setStaffCount} />
        </div>
      </HeaderMotion>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 pb-16 -mt-[220px]">
        <div className="relative isolate mx-auto flex flex-col gap-y-4">
          {/*
            Whe may use below banner in the future
            {parseInt(staffCount) >= maxStaff && (
              <p className='text-center bg-white/50 rounded-lg p-2 mt-8'>Have more than {maxStaff} staff? <a href='#' className='text-midnight-500 underline'>Book a call with our Sales team</a>, we will craft a package to suit you </p>
            )}
          */}

          {!umbraco.IsSimplifiedVersion && (
            <PricingRegionSection
              standardTransition={standardTransition}
              standardViewportTrigger={standardViewportTrigger}
              pricingRegion={pricingRegion}
              setPricingRegion={setPricingRegion}
            />
          )}

          <PricePlansTabs plans={_plans} umbraco={umbraco} />

          <div className="isolate mx-auto w-full grid grid-cols-1 items-stretch gap-y-8 gap-x-3 lg:mx-0 lg:max-w-none lg:grid-cols-3 max-lg:pt-4">
            {_plans.map((plan, index) => (
              <PriceCard
                key={plan}
                pricingRegion={pricingRegion}
                staffCount={staffCount}
                planId={plan}
                cardIndex={index}
                umbraco={umbraco}
              />
            ))}
          </div>

          {/*
          We were asked by the client to hide this for the moment.
          {umbraco.IsSimplifiedVersion && (
            <PricingRegionSection standardTransition={standardTransition} standardViewportTrigger={standardViewportTrigger} pricingRegion={pricingRegion} setPricingRegion={setPricingRegion} isCentered />
          )}
          */}

          {umbraco?.LinkUrl && umbraco?.LinkName && (
            <DivMotion
              className="flex justify-center"
              data-theme="charcoal-light"
              initial={{ translateY: standardTransitionDistance, opacity: 0.1 }}
              whileInView={{ translateY: '0', opacity: 1 }}
              transition={{
                duration: standardTransitionDuration,
                delay: standardTransitionDelay + 4 * standardTransitionStagger,
                ease: standardTransitionEasing,
              }}
              viewport={{ once: true, standardViewportTrigger }}
            >
              <Button href={umbraco.LinkUrl}>{umbraco.LinkName}</Button>
            </DivMotion>
          )}
        </div>
      </div>
    </section>
  );
};

const PricingRegionSection = ({
  standardTransition,
  standardViewportTrigger,
  pricingRegion,
  setPricingRegion,
  isCentered = false,
}) => {
  return (
    <DivMotion
      initial={{ opacity: 0.1 }}
      whileInView={{ opacity: 1 }}
      transition={standardTransition}
      viewport={{ once: true, amount: standardViewportTrigger }}
      className={`flex ${isCentered ? 'justify-center' : 'justify-end'} pb-2`}
    >
      <PricingRegionSelect
        pricingRegion={pricingRegion}
        setRegion={setPricingRegion}
      />
    </DivMotion>
  );
};

const PriceCard = ({
  pricingRegion,
  planId,
  staffCount,
  cardIndex,
  umbraco,
}) => {
  const plan = getPlanByPricingRegion({ pricingRegion, planId });
  const planPrice = getPlanPrice({ pricingRegion, planId, staffCount });

  const isStackedContent = useIsViewPortLessOrEqualTo(1024);
  const {
    standardViewportTrigger,
    standardTransitionDuration,
    standardTransitionDelay,
    standardTransitionDistance,
    standardTransitionEasing,
    standardTransitionStagger,
  } = animationSettings();

  return (
    <DivMotion
      className="h-full"
      initial={{ translateY: standardTransitionDistance, opacity: 0.1 }}
      whileInView={{ translateY: '0', opacity: 1 }}
      transition={{
        duration: standardTransitionDuration,
        delay: isStackedContent
          ? 0
          : standardTransitionDelay + cardIndex * standardTransitionStagger,
        ease: standardTransitionEasing,
      }}
      viewport={{ once: true, standardViewportTrigger }}
      key={plan.id}
    >
      <div
        className={classNames(
          plan.featured
            ? 'bg-midnight-700 ring-midnight-700'
            : 'ring-charcoal-200 bg-white',
          'rounded-2xl py-6 ring-1 w-full h-full',
        )}
      >
        <h3
          id={plan.id}
          className={classNames(
            plan.featured ? 'text-white' : 'text-midnight-700',
            'text-lg font-medium flex justify-between pl-7 pr-5',
          )}
        >
          {plan.name}
          {plan.featured && (
            <span className="px-2 h-6 inline-flex items-center rounded-full border border-white leading-none font-light text-xs">
              <StarIcon className="size-2 -mt-0.5 mr-0.5" />
              Recommended
            </span>
          )}
        </h3>
        <p
          className={classNames(
            plan.featured ? 'text-white' : 'text-charcoal-800',
            'my-4 text-sm px-7 text-pretty lg:h-[4.4rem] xl:h-[3.6rem]',
          )}
          dangerouslySetInnerHTML={{ __html: plan.description }}
        />
        {planPrice.isDisabled && (
          <p
            className={classNames(
              plan.featured ? 'text-white' : 'text-charcoal-800',
              'text-sm font-semibold px-7 pt-7 lg:pt-[54px] lg:h-[91px]',
            )}
          >
            {planId === TimelyPlanName.BUILD
              ? 'Only available for salons with 1-7 staff'
              : 'Book a call for a custom quote'}
          </p>
        )}
        {!planPrice.isDisabled && (
          <>
            <p className="mt-4 flex items-baseline gap-x-1 px-7">
              <span
                className={classNames(
                  plan.featured ? 'text-white' : 'text-charcoal-800',
                  'text-4xl tracking-tight font-serif',
                )}
              >
                {planPrice.sign}
                {numberToString(
                  parseInt(staffCount) >= 8
                    ? planPrice.total
                    : planPrice.total / (parseInt(staffCount) || 1),
                )}
              </span>

              <span className="relative block pl-1">
                <span
                  className={classNames(
                    plan.featured ? 'text-white' : 'text-charcoal-800',
                    'text-xs absolute -top-4 leading-none whitespace-nowrap',
                  )}
                >
                  {parseInt(staffCount) >= 8 ? 'total price' : 'per staff'}
                </span>
                <span
                  className={classNames(
                    plan.featured ? 'text-midnight-200' : 'text-midnight-800',
                    'text-xs block leading-none',
                  )}
                >
                  / monthly
                </span>
              </span>
            </p>
            <div className="h-4" /> {/* spacer */}
            <p className="flex px-7 flex-wrap items-center text-sm">
              <span
                className={classNames(
                  '',
                  plan.featured ? 'text-white' : 'text-charcoal-800',
                  'nowrap font-semibold leading-[1.25]',
                )}
              >
                {parseInt(staffCount) >= 8 ? (
                  <>
                    {planPrice.sign}
                    {numberToString(
                      planPrice.total / (parseInt(staffCount) || 1),
                    )}{' '}
                    {planPrice.currencyCode} per staff {planPrice.priceTag}
                    <br />
                  </>
                ) : (
                  <>
                    Total {planPrice.sign}
                    {numberToString(planPrice.total)} {planPrice.currencyCode}{' '}
                    {planPrice.priceTag}
                    <br />
                  </>
                )}
                <span className="text-charcoal-300 font-normal">
                  Billed monthly
                </span>
              </span>
            </p>
          </>
        )}
        <div className="h-6" /> {/* spacer */}
        <div className="px-5 pt-0.5">
          <Button
            href={REGISTER_PAGE_URL + '?plan=' + plan.id}
            aria-describedby={plan.id}
            variant={plan.featured ? 'default' : 'outline'}
            mobile="block"
            size="sm"
            className={classNames(planPrice.isDisabled ? 'disabled' : '')}
          >
            {plan.cta}
            <ArrowLongRightIcon
              aria-hidden="true"
              className="fill-[currentColor] block size-5 ml-2"
            />
          </Button>
        </div>
        {!umbraco.IsSimplifiedVersion && (
          <>
            <ul
              role="list"
              className={classNames(
                plan.featured ? 'text-white' : 'text-charcoal-800',
                planPrice.isDisabled ? 'hidden lg:block opacity-50' : '',
                'mt-8 space-y-5 text-sm px-7',
              )}
            >
              <li className="flex items-center gap-x-3 ">
                <PlusIcon
                  aria-hidden="true"
                  className="size-5 -mt-1 flex-none text-[currentColor] _icon-stroke"
                />

                <span className="pb-0.5">
                  {planPrice.freeSmsPerStaffPerMonth} SMS{' '}
                  <span
                    className={classNames(
                      plan.featured ? 'text-midnight-200' : 'text-midnight-800',
                    )}
                  >
                    per month included
                    {pricingRegion === PricingRegion.ROW && (
                      <a href="#FAQs">*</a>
                    )}
                  </span>
                </span>
              </li>

              <li className="flex items-center gap-x-3">
                <PlusIcon
                  aria-hidden="true"
                  className="size-5 -mt-1 flex-none text-[currentColor] _icon-stroke"
                />

                <span className="pb-0.5">No new client fees</span>
              </li>
            </ul>

            <hr
              className={classNames(
                plan.featured ? 'border-midnight-500' : 'border-charcoal-200',
                planPrice.isDisabled ? 'hidden lg:block opacity-50' : '',
                'my-4 border-t',
              )}
            />

            <p
              className={classNames(
                plan.featured
                  ? 'text-white'
                  : 'text-charcoal-800 border-charcoal-800',
                planPrice.isDisabled ? 'hidden lg:block opacity-50' : '',
                'text-xs pt-2.5 px-7',
              )}
            >
              {plan.features.title}
            </p>

            <ul
              role="list"
              className={classNames(
                plan.featured ? 'text-white' : 'text-charcoal-800',
                planPrice.isDisabled ? 'hidden lg:block opacity-50' : '',
                'mt-5 space-y-5 pb-2 text-sm px-7',
              )}
            >
              {plan.features.list.map((feature) => (
                <li key={feature} className="flex items-center gap-x-3">
                  <CheckIcon
                    aria-hidden="true"
                    className="size-5 -mt-1 flex-none text-[currentColor] _icon-stroke"
                  />
                  <span
                    className="pb-0.5 text-pretty"
                    dangerouslySetInnerHTML={{ __html: feature }}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </DivMotion>
  );
};

const PricingRegionSelect = ({ pricingRegion, setRegion }) => {
  const selectedCountry = RegionLabels[pricingRegion];

  return (
    <Menu
      as="div"
      className="relative inline-block text-left text-charcoal-800"
    >
      <div>
        <MenuButton className="inline-flex w-full items-center justify-center gap-x-1.5 rounded-lg bg-transparent px-3 py-2 text-base hover:bg-gray-50">
          <span className="relative -top-0.5">{selectedCountry}</span>
          <ChevronDownIcon
            aria-hidden="true"
            className="-mr-1 size-[18px] stroke-[2.5] text-charcoal-800"
          />
        </MenuButton>
      </div>

      <MenuItems
        transition
        className="absolute right-0 z-20 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
      >
        <div className="py-1">
          {Object.entries(RegionLabels).map(([key, value]) => (
            <MenuItem key={key}>
              <button
                onClick={() => setRegion(key)}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 data-[focus]:bg-gray-100 data-[focus]:text-gray-900 data-[focus]:outline-none"
              >
                {value}
              </button>
            </MenuItem>
          ))}
        </div>
      </MenuItems>
    </Menu>
  );
};

const StaffCounter = ({ staffCount, setStaffCount }) => {
  const increment = () => {
    setStaffCount((prev) => {
      if (isNaN(parseInt(prev))) return '1';
      if (prev === maxStaff + '+') return prev;
      const _prev = parseInt(prev);
      const total = _prev < maxStaff ? _prev + 1 : prev;
      if (total === maxStaff) return maxStaff + '+';
      return total;
    });
  };

  const decrement = () => {
    setStaffCount((prev) => {
      if (isNaN(parseInt(prev))) return '1';
      if (prev === 1) return prev;
      const _prev = parseInt(prev);
      const total = _prev > 1 ? _prev - 1 : prev;
      return total;
    });
  };

  const handleOnChange = (e) => {
    const value = e.target.value.replace(/[^0-9+]/g, '').slice(0, 2);
    setStaffCount(value);
  };

  const handleOnKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      increment();
    } else if (e.key === 'ArrowDown') {
      decrement();
    }
  };

  const StaffIcon =
    parseInt(staffCount) <= 2
      ? UserIcon
      : parseInt(staffCount) <= 5
        ? UsersIcon
        : UserGroupIcon;

  return (
    <div>
      <p className="text-charcoal-800 text-center text-lg font-medium pt-8 mb-2">
        How many bookable staff?
      </p>
      <div className="inline-flex items-center gap-2">
        <button onClick={decrement} className="p-2">
          <span className="sr-only">Decrement</span>
          <MinusIcon className="size-6 text-white rounded-full bg-midnight-700 [@media(hover:hover)]:[@media(pointer:fine)]:hover:active:scale-100 [@media(hover:hover)]:[@media(pointer:fine)]:hover:scale-110 [@media(hover:hover)]:[@media(pointer:fine)]:hover:bg-midnight-700" />
        </button>
        <div className="-mr-px grid grow grid-cols-1 focus-within:relative">
          <label htmlFor="query" className="sr-only">
            Staff count
          </label>
          <input
            id="query"
            name="query"
            type="text"
            value={staffCount}
            onChange={handleOnChange}
            onKeyDown={handleOnKeyDown}
            className="col-start-1 row-start-1 block w-20 text-center rounded-lg bg-white py-4 pl-10 pr-3 text-base text-charcoal-800 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-charcoal-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-midnight-700"
          />
          <StaffIcon
            aria-hidden="true"
            className="pointer-events-none col-start-1 row-start-1 mx-3 size-5 self-center text-charcoal-500"
          />
        </div>
        <button onClick={increment} className="p-2">
          <span className="sr-only">Increment</span>
          <PlusIcon className="size-6 text-white rounded-full bg-midnight-700 [@media(hover:hover)]:[@media(pointer:fine)]:hover:active:scale-100 [@media(hover:hover)]:[@media(pointer:fine)]:hover:scale-110 [@media(hover:hover)]:[@media(pointer:fine)]:hover:bg-midnight-700" />
        </button>
      </div>
    </div>
  );
};

const PricePlansTabs = ({ plans, umbraco }) => {
  const { standardTransition, standardViewportTrigger } = animationSettings();

  // Track active tab, default to first
  const [activeTab, setActiveTab] = useState(plans[0]);

  return (
    <DivMotion
      initial={{ opacity: 0.1 }}
      whileInView={{ opacity: 1 }}
      transition={standardTransition}
      viewport={{ once: true, amount: standardViewportTrigger }}
      className={`lg:hidden sticky top-16 z-10 -mx-6 ${umbraco.IsSimplifiedVersion ? 'bg-charcoal-50' : 'bg-midnight-50'}`}
    >
      <div className="px-6">
        <nav
          aria-label="Tabs"
          className="-mb-px flex border-b border-charcoal-200"
        >
          {plans.map((tab) => (
            <a
              key={tab}
              href={'#' + tab}
              onClick={() => setActiveTab(tab)}
              className={classNames(
                activeTab === tab
                  ? 'border-midnight-700'
                  : 'border-transparent text-charcoal-800 hover:border-gray-300 hover:text-gray-700',
                'w-1/3 border-b-2 px-1 py-4 text-lg font-medium capitalize flex items-baseline justify-center gap-1',
              )}
            >
              {tab === TimelyPlanName.INNOVATE && (
                <StarIcon className="text-midnight-700 size-3" />
              )}
              {tab}
            </a>
          ))}
        </nav>
      </div>
    </DivMotion>
  );
};

reactMount('PricePlansV1', PricePlansV1);
